/**
 * Typed API client + TanStack Query hooks for the backend.
 *
 * Types come from `src/api.gen.ts` — auto-generated by `pnpm gen:api`
 * from the backend's OpenAPI spec at `frontend/openapi.json`. CI runs
 * the drift check (`task openapi:check`) so the committed
 * `openapi.json` and the live backend spec stay in lock-step; if a
 * route changes, regenerate with `task openapi:gen`.
 *
 * Runtime validation is intentionally not enforced here — the spec is
 * the contract, the backend is the contract owner, and the integration
 * tests (`tests/integration/test_*_api.py`) hold both sides honest. If
 * a payload ever drifts at runtime we'd rather see the inevitable
 * TypeError than silently coerce.
 */

import { useQuery } from "@tanstack/react-query";

import { getAccessToken } from "@/auth";
import type { components } from "@/api.gen";
import { env } from "@/env";

// ─── Re-exports so the rest of the SPA imports from one place ───

export type RiskTier = components["schemas"]["RiskTier"];
export type ChainSummary = components["schemas"]["ChainSummary"];
export type ChainLoadOut = components["schemas"]["ChainLoadOut"];
export type ChainDetail = components["schemas"]["ChainDetail"];
export type ChainListResponse = components["schemas"]["ChainListResponse"];
export type StatsResponse = components["schemas"]["StatsResponse"];
export type UserResponse = components["schemas"]["UserResponse"];
export type HealthResponse = components["schemas"]["HealthResponse"];
export type MarketInfo = components["schemas"]["MarketInfoOut"];
export type MarketListResponse = components["schemas"]["MarketListResponse"];
export type CityInfo = components["schemas"]["CityInfoOut"];
export type CityListResponse = components["schemas"]["CityListResponse"];
export type ChainGraph3DResponse = components["schemas"]["ChainGraph3DResponse"];
export type ChainGraph3DNode = components["schemas"]["ChainGraph3DNode"];
export type ChainGraph3DEdge = components["schemas"]["ChainGraph3DEdge"];

/** Polyline = list of [lat, lng] pairs (matches the OpenAPI schema). */
export type Polyline = ChainLoadOut["loaded_polyline"];

// Query params — small typed surfaces; the rest of the request shape
// (paths, bodies) lives in api.gen.ts.
export interface ChainListParams {
  k?: number;
  origin_market?: string;
  destination_market?: string;
  max_deadhead_mi?: number;
  min_slack_min?: number;
  max_duration_days?: number;
  risks?: string;
  /** ISO date (`YYYY-MM-DD`). Filters to chains whose first-pickup date
   *  equals this. Combined with `chain_end_date` it pins the window. */
  pickup_date?: string;
  /** ISO date (`YYYY-MM-DD`). Filters to chains whose final-drop date
   *  equals this. Combined with `pickup_date` it pins the window. */
  chain_end_date?: string;
  page?: number;
  page_size?: number;
}

export interface CityListParams {
  q?: string;
  limit?: number;
}

export interface ChainGraph3DParams {
  /** Strict per-handoff deadhead cap (miles). Defaults to 100 on the
   *  backend; omit to let the server pick. */
  max_deadhead_mi?: number;
  /** Include chains up to this length (defaults to 4). */
  max_k?: number;
}

// ─── Client ─────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function buildUrl(path: string, query?: Record<string, unknown>): string {
  const url = new URL(path, env.apiBaseUrl || window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      if (Array.isArray(v)) {
        url.searchParams.set(k, v.join(","));
      } else {
        url.searchParams.set(k, String(v));
      }
    }
  }
  return url.toString();
}

async function request<T>(
  path: string,
  init?: RequestInit & { query?: Record<string, unknown> },
): Promise<T> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...((init?.headers as Record<string, string>) ?? {}),
  };

  const response = await fetch(buildUrl(path, init?.query), { ...init, headers });
  const bodyText = await response.text();
  const body: unknown = bodyText ? JSON.parse(bodyText) : null;

  if (!response.ok) {
    throw new ApiError(
      `${response.status} ${response.statusText} on ${path}`,
      response.status,
      body,
    );
  }
  return body as T;
}

// ─── Endpoints ───────────────────────────────────────────────────

export const api = {
  me: () => request<UserResponse>("/v1/me"),
  stats: () => request<StatsResponse>("/v1/stats"),
  health: () => request<HealthResponse>("/healthz"),
  chains: (params: ChainListParams) =>
    request<ChainListResponse>("/v1/chains", {
      query: params as Record<string, unknown>,
    }),
  chainDetail: (chainId: string) =>
    request<ChainDetail>(`/v1/chains/${encodeURIComponent(chainId)}`),
  chainGraph3D: (params: ChainGraph3DParams = {}) =>
    request<ChainGraph3DResponse>("/v1/chains/graph-3d", {
      query: params as Record<string, unknown>,
    }),
  markets: () => request<MarketListResponse>("/v1/markets"),
  cities: (params: CityListParams = {}) =>
    request<CityListResponse>("/v1/cities", {
      query: params as Record<string, unknown>,
    }),
};

// ─── Query hooks ────────────────────────────────────────────────

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => api.me(),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useStats() {
  return useQuery({
    queryKey: ["stats"],
    queryFn: () => api.stats(),
    refetchInterval: 60_000,
  });
}

export function useChains(params: ChainListParams) {
  return useQuery({
    queryKey: ["chains", params],
    queryFn: () => api.chains(params),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}

export function useChainDetail(chainId: string | undefined) {
  return useQuery({
    queryKey: ["chain", chainId],
    queryFn: () => {
      if (!chainId) throw new Error("missing chainId");
      return api.chainDetail(chainId);
    },
    enabled: !!chainId,
  });
}

/** Full market list — small, fetched once on mount, refreshed every 5 min. */
export function useMarkets() {
  return useQuery({
    queryKey: ["markets"],
    queryFn: () => api.markets(),
    staleTime: 5 * 60_000,
  });
}

/** Typeahead-friendly city search. Empty query returns the top-N cities. */
export function useCities(params: CityListParams) {
  return useQuery({
    queryKey: ["cities", params],
    queryFn: () => api.cities(params),
    staleTime: 30_000,
  });
}

/** 3D chain-network projection. The page sets `staleTime` high since the
 *  graph is expensive to render and the underlying snapshot only refreshes
 *  every ~30 min. */
export function useChainGraph3D(params: ChainGraph3DParams) {
  return useQuery({
    queryKey: ["chain-graph-3d", params],
    queryFn: () => api.chainGraph3D(params),
    staleTime: 5 * 60_000,
  });
}
